import {
  ApolloClient,
  ApolloLink,
  concat,
  createHttpLink,
  InMemoryCache,
  Observable,
  split,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { onError } from "@apollo/client/link/error";
import { createClient } from "graphql-ws";
import ConfigurableValues from "../config/constants";
import { isAuthError, notifySessionExpired } from "../utils/session";
import { perfDebugEnabled, recordOp } from "../utils/perfDebug";

const setupApollo = () => {
  const { SERVER_URL, WS_SERVER_URL } = ConfigurableValues();
  // keyFields: false → treat these as embedded objects, never merge across parents.
  // Without this, two order items sharing the same addon group _id get their
  // options overwritten in the cache, causing both items to show identical addons.
  //
  // The advanced-menu types below MUST also opt out: the SAME item/modifier _id
  // appears in several menus, but each menu returns it with DIFFERENT prices
  // (basePrice/variantPrices/additionalCost/fixedPrice already carry that menu's
  // display markup). With default normalization Apollo merges all copies into one
  // cache entry by _id, so the last/first menu's prices clobber the rest — e.g. a
  // 10%-markup "lunch" item shows the 0%-markup "Breakfast" price. keyFields:false
  // keeps each menu's copy embedded under its own menu, preserving per-menu prices.
  const cache = new InMemoryCache({
    typePolicies: {
      OrderItem:  { keyFields: false },
      OrderAddon: { keyFields: false },
      // OrderOption must be un-normalised for the same reason OrderAddon is.
      // myOrders selects `_id` on options, and a modifier keeps the SAME
      // modifierId wherever it appears — so a topping picked at the top level
      // and again inside a nested group normalised to one cache entity and the
      // last write won. On a real order that showed bonda at its nested
      // per-unit $0.50 instead of $1.00, dropped the nested group it revealed,
      // and left the line total at $29.50 against a $32.00 server value (the
      // receipt's own lines then no longer summed to its Subtotal).
      OrderOption: { keyFields: false },
      AdvancedMenuItem:    { keyFields: false },
      AdvancedModifierGroup: { keyFields: false },
      ModifierGroupItem:   { keyFields: false },
    },
  });
  const httpLink = createHttpLink({
    uri: `${SERVER_URL}graphql`,
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: `${WS_SERVER_URL}graphql`,
    })
  );

  const request = async (operation) => {
    const data = localStorage.getItem("token");

    let token = null;
    if (data) {
      token = data;
    }
    // Merge rather than assign: an outer link may already have put headers on
    // the context (the perf probe's x-perf-trace), and a plain object would
    // silently drop them.
    // Inside a partner-site embed or short link (context/Embed.js pins the
    // restaurant in sessionStorage), tell the server, so an order placed here
    // is recorded as an embed order for the super admin's channel figures.
    let embedded = false;
    try {
      embedded = !!sessionStorage.getItem("embedRestaurant");
    } catch (e) {
      embedded = false;
    }
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
        ...(embedded ? { "x-rmf-channel": "embed" } : {}),
      },
    }));
  };

  const requestLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let handle;
        Promise.resolve(operation)
          .then((oper) => request(oper))
          .then(() => {
            handle = forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            });
          })
          .catch(observer.error.bind(observer));

        return () => {
          if (handle) handle.unsubscribe();
        };
      })
  );

  // A token that has expired (or was invalidated server side) makes every authed
  // operation fail with "Unauthenticated". Left unhandled the customer keeps a
  // signed-in looking UI with no profile, no saved addresses, and a cryptic
  // error at Place Order. Catch it once here and let UserContext turn it into a
  // normal signed-out state with a "please sign in again" message.
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    const rejected =
      (graphQLErrors || []).some((err) => isAuthError(err)) ||
      networkError?.statusCode === 401;
    if (!rejected) return;
    // Only a session that was supposed to be valid counts. An anonymous visitor
    // touching a protected field is a routing issue, not an expired login, and
    // must not trigger the sign-in prompt.
    if (!localStorage.getItem("token")) return;
    notifySessionExpired();
  });

  const terminatingLink = split(({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === "OperationDefinition" && operation === "subscription";
  }, wsLink);

  // Debug only (?debug=1): time every operation that actually hits the network.
  // Outermost in the chain so it measures what the screen waits for, and absent
  // entirely otherwise. Cache hits never reach a link, so anything listed here
  // is a real round trip.
  const timingLink = new ApolloLink((operation, forward) => {
    // Subscriptions are long-lived streams, not round trips: their "duration"
    // is however long the socket stayed open, which made a 51s OrderStatusChanged
    // outrank every real query in the summary bar. Let them through untimed.
    const def = getMainDefinition(operation.query);
    if (def.kind === "OperationDefinition" && def.operation === "subscription") {
      return forward(operation);
    }

    const startedAt = performance.now();
    // Asks the backend for its own breakdown. Only this link ever sets it, so a
    // customer's request is never traced.
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, "x-perf-trace": "1" },
    }));
    return forward(operation).map((result) => {
      recordOp({
        name: operation.operationName || "(anonymous)",
        startedAt: Math.round(startedAt),
        ms: Math.round(performance.now() - startedAt),
        // Uncompressed JSON, not wire bytes — it is what the browser must parse.
        bytes: JSON.stringify(result.data || {}).length,
        error: !!(result.errors && result.errors.length),
        // Phase + SQL breakdown from the server, when it sent one.
        server: result.extensions?.perf || null,
      });
      return result;
    });
  });

  const links = [errorLink, terminatingLink, requestLink];
  if (perfDebugEnabled) links.unshift(timingLink);

  const client = new ApolloClient({
    link: concat(ApolloLink.from(links), httpLink),
    cache,
    resolvers: {},
    connectToDevTools: true,
  });

  return client;
};

export default setupApollo;
